/*
 * Copyright (c) 2022 Honeywell International, Inc. All rights reserved.
 *
 * This file contains trade secrets of Honeywell International, Inc.  No part
 *  may be reproduced or transmitted in any form by any means or for any
 *  purpose without the express written permission of Honeywell.
 *
 */

import React, { useEffect, useState } from "react";
import { IFCPDIYGatewayManagementProps } from "../stores/GatewayManager/IGatewayRegistrationInterfaces";
import {
  ICondition,
  loadOnConditionsSatisfied,
} from "./common/ConditionalLoader";
import { DIYErrorPage } from "./common/DIYErrorPage";
import fcpFeatureFlagsStore from "../utils/FeatureFlags/FCPFeatureFlagsStore";
import { FCPDIYGatewayManagementHeader } from "../pages/GatewayManager/FCPDIYGatewayManagementHeader";
import { ContactSupport } from "./common/ContactSupport";
import { inject } from "mobx-react";
import { IGatewayDetail } from "./gateway/registration/IGatewayDetails";
import { LDUser } from "launchdarkly-js-client-sdk";
import GatewayRegistrationStore from "../stores/GatewayManager/GatewayRegistrationStore";
import FCDIYGatewayRegistration from "./gateway/registration/FCDIYGatewayRegistration";
import FCDIYApplianceDetails from "./gateway/registration/FCDIYApplianceDetails";
/**
 * Properties for Launch FC DIY gateway management component
 */
export interface IFCLaunchDIYGatewayManagementProps
  extends IFCPDIYGatewayManagementProps {
  conditions?: ICondition[];
}

/**
 * the entry point for DIY application for appliance gateway in FCP flow
 * @constructor
 */
export const FCPDIYGatewayManagementAppInternal = (
  launchProps: IFCLaunchDIYGatewayManagementProps
) => {
  const [conditions, setConditions] = useState(
    launchProps.conditions || ([] as ICondition[])
  );
  const [showSupport, setShowSupport] = useState(false);
  const LoadFCDIYGatewayManagement = loadOnConditionsSatisfied(
    FCPDIYGatewayManagement,
    DIYErrorPage
  );

  const [featureFlagInitialized, setFeatureFlagInitialized] = useState(false);

  /**
   * Initialize the feature flag store
   */
  useEffect(() => {
    fcpFeatureFlagsStore.init(() => setFeatureFlagInitialized(true));
  }, []);
  /**
   * Evaluate whether the feature flag is enabled at tenant level
   */
  useEffect(() => {
    if (featureFlagInitialized) {
      setConditions([
        {
          execute: () => {
            return Promise.resolve(fcpFeatureFlagsStore.getFCPEPMDIYAppEnabled);
          },
        },
      ]);
    }
  }, [featureFlagInitialized]);

  /**
   * Display contact support
   */
  const launchContactSupport = () => {
    setShowSupport(true);
  };

  let envVariables = launchProps.globalStore!.envVariables;
  let phNumbers =
    (envVariables.FCP_SUPPORT_PHONE_NUMBERS &&
      envVariables.FCP_SUPPORT_PHONE_NUMBERS.split(",")) ||
    [];

  return (
    <div className="root">
      <FCPDIYGatewayManagementHeader
        logout={launchProps.logout}
        userInfo={launchProps.userInfo}
      />
      <div className="application-container">
        <div className="application-main-content">
          {featureFlagInitialized && conditions.length > 0 ? (
            showSupport ? (
              <ContactSupport
                supportEmail={envVariables.FCP_SUPPORT_EMAIL_ID}
                supportPhoneNumbers={phNumbers}
                onClose={() => setShowSupport(false)}
              />
            ) : (
              <LoadFCDIYGatewayManagement
                {...launchProps}
                title={"Launching application"}
                errorTitle={"Service not available...!"}
                message={
                  "If this repeats, contact support or retry after sometime."
                }
                showSupportLink={true}
                supportHandler={() => launchContactSupport()}
                conditions={conditions}
              />
            )
          ) : (
            <></>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * The actual SPA sceens and their interactions will be coded here
 * @param props
 * @constructor
 */
export const FCPDIYGatewayManagement = (
  props: IFCPDIYGatewayManagementProps
) => {
  let gateways: IGatewayDetail[] = [];
  const [gws, setGateways] = useState(gateways);
  const [featureFlagInitialized, setFeatureFlagInitialized] = useState(false);
  const user: LDUser = fcpFeatureFlagsStore.getLDUserInfo();
  const userProjectId = ((user.custom && user.custom.tenant) ? user.custom.tenant :"") as string
  useEffect(() => {
    fcpFeatureFlagsStore.init(() => {
      setFeatureFlagInitialized(true);
    });
  }, []);
  useEffect(() => {
    getGatewayList(userProjectId, setGateways);
  }, []);

  if (featureFlagInitialized) {
    return renderGatewayRegistration(props, gws, user);
  } else {
    return <div />;
  }
};

const parseGatewaysResponse = (gatewayListObject: any) => {
  let gatewayList: Array<IGatewayDetail> = [];
  let gateways = JSON.parse(JSON.stringify(gatewayListObject)).data;
  gateways.forEach((gateway: any, idx: number) => {
    gatewayList.push({
      gatewayName: gateway["gatewayName"],
      serialNumber: gateway["serialNumber"],
      gatewayType: gateway["gatewayType"],
      externalId: gateway["externalId"],
      gatewayStatus: gateway["gatewayStatus"],
      softwareInfo: gateway["softwareInfo"],
      fcGatewayExternalId: gateway["fcGatewayExternalId"],
    });
  });
  return gatewayList;
};

const getGatewayList = (
  projectId: string = "",
  setGateways: (gatewayListObject: any) => void
) => {
  let gatewayList: Array<IGatewayDetail> = [];
  const gatewayStore: GatewayRegistrationStore = new GatewayRegistrationStore();
  gatewayStore
    .getGateways(projectId)
    .then((response) => {
      const gatewayListObject = response.data;
      if (gatewayListObject && response.status == 200)
        setGateways(parseGatewaysResponse(gatewayListObject));
    })
    .catch((exception) => console.log(exception));
  return gatewayList;
};

const renderGatewayRegistration = (
  props: IFCPDIYGatewayManagementProps,
  gateways: IGatewayDetail[],
  user: LDUser
) => {

  if(gateways.length == 0)
  {
    const tenant = (user.custom && user.custom.tenant) ? user.custom.tenant : "";
    return <div className="application-page-content">
      <h1>There are no gateways available to register under <br/>{tenant} .</h1>
      </div>;
  }

  const enrollmentIdDecoded = decodeURIComponent(`${sessionStorage.getItem("enrId")}`)
  return (
    <div>
      <div className="application-container">
        <div className="application-main-content">
          <FCDIYGatewayRegistration
            appliance={
              new FCDIYApplianceDetails(
                `${sessionStorage.getItem("sn")}`,
                `${sessionStorage.getItem("epName")}`,
                `${enrollmentIdDecoded}`
              )
            }
            details={gateways}
            userInfo={user}
          ></FCDIYGatewayRegistration>
        </div>
      </div>
    </div>
  );
};

/**
 * the diy app entry point with global store injected.
 */
export const FCPDIYGatewayManagementApp = inject('globalStore')(FCPDIYGatewayManagementAppInternal);
